// v4 boulder — REDESIGNED. One BIG rounded mass of weathered grey stone:
// a great dome with a lower shoulder grown into it and rubble strewn at the
// foot. Variant 0 is the whole mass, variant 1 is visibly SPLIT — a calved
// slice leans away from the dome over a dark cleft — and variant 2 is a
// lower, wind-worn dome with a capstone perched on top.
//
// Ported 1:1 from age-of-mcp's v4/boulder.ts. The v4 `scree` helper (with its
// `h01`/`lerp`) is inlined locally because v4's variant differs from the
// library's `mineral.scree`.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

function h01(seed: number, n: number): number {
  const x = Math.sin(seed * 117.32 + n * 271.7 + 31.4) * 43758.5453;
  return x - Math.floor(x);
}
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// A ring of small rubble stones — a mix of pebbles and tilted shards — strewn
// around the foot of a rock mass.
function scree(
  seed: number, count: number,
  dist: readonly [number, number], size: readonly [number, number],
  colors: readonly string[],
): Part[] {
  const parts: Part[] = [];
  for (let i = 0; i < count; i++) {
    const ang = (i / count) * Math.PI * 2 + h01(seed, i * 4) * 1.6;
    const d = lerp(dist[0], dist[1], h01(seed, i * 4 + 1));
    const r = lerp(size[0], size[1], h01(seed, i * 4 + 2));
    const shard = i % 2 === 1;
    parts.push({
      geometry: shard
        ? new THREE.TetrahedronGeometry(r * 0.95, 0)
        : new THREE.IcosahedronGeometry(r, 0),
      color: colors[i % colors.length]!,
      position: [Math.cos(ang) * d, shard ? r * 0.28 : r * 0.5, Math.sin(ang) * d],
      rotation: [
        h01(seed, i * 4 + 3) * 0.9,
        h01(seed, i * 7 + 11) * Math.PI * 2,
        h01(seed, i * 5 + 23) * 0.9,
      ],
      scale: [1.05, 0.92, 1.0],
    });
  }
  return parts;
}

function partsFor(v: number): Part[] {
  const tint = ["#7e828a", "#888b91", "#74777e"][v % 3]!;
  const shade = "#696c74";
  const parts: Part[] = [];

  if (v === 1) {
    // split boulder — the dome plus a calved slice leaning away
    parts.push(
      { geometry: new THREE.IcosahedronGeometry(0.46, 0), color: tint,
        position: [-0.12, 0.32, 0], rotation: [0, 0.45, 0.06],
        scale: [1.0, 0.92, 1.05] },
      // the slice that broke off, tipped outward
      { geometry: new THREE.IcosahedronGeometry(0.36, 0), color: shade,
        position: [0.33, 0.26, 0.02], rotation: [0.05, 1.2, -0.22],
        scale: [0.55, 0.9, 0.95] },
      // the dark cleft between the halves
      { geometry: new THREE.BoxGeometry(0.05, 0.5, 0.66), color: "#3e4147",
        position: [0.14, 0.28, 0], rotation: [0, 0.1, -0.1] },
    );
  } else if (v === 2) {
    // weathered — lower and wider, a capstone perched on the crown
    parts.push(
      { geometry: new THREE.IcosahedronGeometry(0.5, 0), color: tint,
        position: [0, 0.3, 0], rotation: [0, 0.8, 0],
        scale: [1.28, 0.78, 1.14] },
      { geometry: new THREE.IcosahedronGeometry(0.16, 0), color: shade,
        position: [0.03, 0.66, -0.04], rotation: [0.3, 1.7, 0.15],
        scale: [1.1, 0.75, 1.0] },
    );
  } else {
    // the whole mass — a great dome with a shoulder grown into it
    parts.push(
      { geometry: new THREE.IcosahedronGeometry(0.5, 0), color: tint,
        position: [-0.04, 0.33, 0], rotation: [0, 0.3, 0.04],
        scale: [1.16, 0.9, 1.06] },
      { geometry: new THREE.IcosahedronGeometry(0.33, 0), color: shade,
        position: [0.3, 0.2, 0.1], rotation: [0.1, 1.5, 0],
        scale: [1.0, 0.85, 0.95] },
    );
  }

  // rubble at the foot
  parts.push(...scree(v * 11 + 3, 3, [0.4, 0.5], [0.06, 0.1],
    [tint, shade, "#8d9097"]));
  return parts;
}

export const boulderV4 = defineModel({
  id: "boulder-v4",
  batchId: "polished",
  name: "Boulder (v4)",
  tags: ["scenery","boulder","low-poly","age-of-mcp","fable"],
  summary: "Low-poly boulder.",
  description: "Ported from the age-of-mcp gallery, v4 style.",
  author: "age-of-mcp",
  footprint: 0.55,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => partsFor(0) },
    { id: "variant-1", label: "Variant 2", parts: () => partsFor(1) },
    { id: "variant-2", label: "Variant 3", parts: () => partsFor(2) },
  ],
  animations: [],
});
